import React from "react";
import PhoneInput, { getCountries } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import ar from "react-phone-number-input/locale/ar.json";
import de from "react-phone-number-input/locale/de.json";
import en from "react-phone-number-input/locale/en.json";
import es from "react-phone-number-input/locale/es.json";
import it from "react-phone-number-input/locale/it.json";
import tr from "react-phone-number-input/locale/tr.json";
import styles from "./SurveyWidgetPanel.module.scss";

const COUNTRY_LABELS = { ar, de, en, es, it, tr };

// A survey written in one of these languages is mostly answered from that country.
const LANGUAGE_COUNTRY = { tr: "TR", de: "DE", it: "IT", es: "ES" };

const baseLanguage = (language) => String(language || "").split("-")[0].toLowerCase();

/**
 * The country the phone field starts on: the survey language's country (Turkish → +90), otherwise the
 * region of the visitor's browser language (en-GB → +44), otherwise none (the visitor picks).
 */
export function defaultPhoneCountry(language, browserLanguages) {
  const fromSurvey = LANGUAGE_COUNTRY[baseLanguage(language)];
  if (fromSurvey) return fromSurvey;
  const known = getCountries();
  for (const tag of browserLanguages || []) {
    const region = String(tag || "").split("-")[1];
    if (region && known.includes(region.toUpperCase())) return region.toUpperCase();
  }
  return undefined;
}

// The library's default flags are images on purecatamphetamine.github.io; a survey page must not send
// its visitors to a third-party host, so the flag is the country's emoji (letters where there is none).
const EmojiFlag = ({ country, countryName }) => (
  <span className={styles.phoneFlag} role="img" aria-label={countryName}>
    {String(country || "")
      .toUpperCase()
      .replace(/[A-Z]/g, (ch) => String.fromCodePoint(0x1f1a5 + ch.charCodeAt(0)))}
  </span>
);

const browserLanguages = () =>
  typeof navigator === "undefined" ? [] : navigator.languages || [navigator.language];

/** One-line phone number with a country-code picker; the value is E.164 (+905551234567). */
const PhoneAnswer = ({
  value,
  onChange,
  onBlur,
  language,
  placeholder,
  invalid,
  labelledBy,
  required,
  disabled,
}) => (
  <PhoneInput
    className={[styles.phoneInput, invalid ? styles.phoneInputInvalid : ""].filter(Boolean).join(" ")}
    international
    countryCallingCodeEditable={false}
    defaultCountry={defaultPhoneCountry(language, browserLanguages())}
    labels={COUNTRY_LABELS[baseLanguage(language)] || en}
    flagComponent={EmojiFlag}
    value={value || ""}
    onChange={(next) => onChange?.(next || "")}
    placeholder={placeholder}
    disabled={disabled}
    numberInputProps={{
      onBlur,
      "aria-labelledby": labelledBy,
      "aria-required": required || undefined,
      "aria-invalid": invalid || undefined,
    }}
  />
);

export default PhoneAnswer;
